import { enableProdMode, provideZoneChangeDetection } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { Environment } from '@env/environment.enum';
import * as Sentry from "@sentry/angular";
import { AppModule } from './app/app.module';
import { ConfigKeycloak, fusionnerConfigKeycloak } from './services/config-keycloak';
import { environment } from './environments/environment';

const SKIN_CLASSES = ['galaclick-skin', 'galaclick-dev-skin', 'galaclick-test-skin'];

// Applique le skin (bordure/couleur) correspondant a l'environnement charge au runtime,
// en remplacement des index-dev.html / index-qualif.html generes au build.
function applySkinClass(): void {
    const skin = environment.skin ?? environment.environment;
    const skinClass = (skin === Environment.PROD) ? 'galaclick-skin'
        : (skin === Environment.QUALIF) ? 'galaclick-test-skin'
            : 'galaclick-dev-skin';
    document.body.classList.remove(...SKIN_CLASSES);
    document.body.classList.add(skinClass);
}

// La config buildee (environment.prod.ts) sert de valeurs par defaut : si assets/config.json
// n'est pas surchargeable (ng serve, config.json non substitue par le conteneur), l'app reste utilisable.
async function loadRuntimeConfig(): Promise<void> {
    try {
        const response = await fetch('assets/config.json', { cache: 'no-store' });
        if (response.ok) {
            const { keycloak, ...config } = await response.json() as { keycloak?: Partial<ConfigKeycloak> } & Record<string, unknown>;
            Object.assign(environment, config);
            environment.keycloak = fusionnerConfigKeycloak(environment.keycloak, keycloak);
        }
    } catch {
        // assets/config.json indisponible : on garde la config buildee
    }
}

async function bootstrap(): Promise<void> {
    await loadRuntimeConfig();
    applySkinClass();

    Sentry.init({
        dsn: environment.sentryDSN,
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({ maskAllText: false, maskAllInputs: false, maxReplayDuration: 300000 })
            // 5 minutes max replay
        ],
        enabled: environment.logSentry,
        environment: environment.name,
        release: ((environment.environment === Environment.DEV) ? "galaclick-www@dev" : "galaclick-www@" + environment.version),
        // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ["localhost", /^https:\/\/galaclick\.es-dev\.estel-numerique\.com\/ws/,
            environment.wsURL],
        // Performance Monitoring
        //  Capture 100% (1.0) of the transactions
        tracesSampleRate: ((environment.environment === Environment.PROD) ? 0.1 : 0),
        // Session Replay
        //replaysSessionSampleRate: ((environment.environment === Environment.PROD) ? 0.1 : 0), // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });

    if (environment.production) {
        enableProdMode();
    }

    platformBrowserDynamic().bootstrapModule(AppModule, { applicationProviders: [provideZoneChangeDetection()], })
        .catch(err => console.log(err));
}

bootstrap();
